import { forwardRef, type ButtonHTMLAttributes } from "react";
import { clsx } from "clsx";
import { Loader } from "./Loader";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  /** Row 21.1 — shows the shared Loader in place of the icon/label, disables the button. */
  loading?: boolean;
}

const loaderSizeForButton: Record<ButtonSize, "sm" | "md"> = { sm: "sm", md: "sm", lg: "md" };
const loaderToneForVariant: Record<ButtonVariant, "inverse" | "brand"> = {
  primary: "inverse",
  secondary: "brand",
  ghost: "brand",
  danger: "inverse",
};

const base =
  "inline-flex items-center justify-center font-medium rounded-md transition-colors " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 " +
  "focus-visible:ring-[var(--color-accent)] disabled:opacity-50 disabled:cursor-not-allowed";

const variants: Record<ButtonVariant, string> = {
  primary:
    "bg-[var(--color-accent)] text-[var(--color-text-inverse)] hover:bg-[var(--color-accent-hover)]",
  secondary:
    "bg-[var(--color-surface-muted)] text-[var(--color-text-primary)] hover:bg-[var(--color-surface-strong)] border border-[var(--color-border-default)]",
  ghost:
    "bg-transparent text-[var(--color-text-primary)] hover:bg-[var(--color-surface-muted)]",
  danger:
    "bg-[var(--color-danger-500)] text-[var(--color-text-inverse)] hover:opacity-90",
};

const sizes: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-sm gap-1",
  md: "h-10 px-4 text-sm gap-2",
  lg: "h-12 px-6 text-base gap-2",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = "primary", size = "md", fullWidth, loading, disabled, className, children, ...props }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={clsx(base, variants[variant], sizes[size], fullWidth && "w-full", className)}
      {...props}
    >
      {loading ? (
        // No `label` passed through — repeating the button's own action text ("Save") next
        // to a spinner reads as ambiguous ("is it done or still saving?"). Loader's own
        // default aria-label="Loading" carries the accessible name instead.
        <Loader inline size={loaderSizeForButton[size]} tone={loaderToneForVariant[variant]} />
      ) : (
        children
      )}
    </button>
  ),
);
Button.displayName = "Button";
